import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { AppLoader } from '@/components/AppLoader'
import { AppShell } from '@/components/layout/AppShell'
import { LoginPage } from '@/pages/Login'
import { OutlookReturn } from '@/pages/OutlookReturn'
import { AuthCallback } from '@/pages/AuthCallback'
import { featureRoutes } from '@/features/registry'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading, dbReady } = useAuth()
  // Stay on the loader while auth resolves AND, once signed in, until the
  // database has answered — overnight the cluster scales to zero, and this
  // makes a first late-night login look like a slightly slow sign-in instead
  // of a shell full of errors. (The resume is usually already underway, kicked
  // off when the user clicked "Sign in with Microsoft".)
  if (loading || (user && !dbReady)) {
    // `waking` once auth has resolved a user but the DB hasn't answered yet —
    // that's the scale-to-zero resume, where the cheeky rotating caption shows.
    return <AppLoader waking={!loading && !!user && !dbReady} />
  }
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { user } = useAuth()
  if (!user || !roles.includes(user.role)) return <Navigate to="/" replace />
  return <>{children}</>
}

export function App() {
  // Routes are contributed by feature slices via @/features/registry — App.tsx
  // never lists individual pages, so feature teams don't collide on this file.
  // To add a route, edit your feature's routes.tsx (see features/registry.ts).
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/outlook/return" element={<OutlookReturn />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {featureRoutes.map((r, i) => (
          <Route
            key={r.index ? '__index' : (r.path ?? i)}
            index={r.index}
            path={r.path}
            element={r.roles ? <RequireRole roles={r.roles}>{r.element}</RequireRole> : r.element}
          />
        ))}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}
